import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { createPortal } from 'react-dom';
import RequiredMark, { withRequiredMark } from '@/shein-platform/components/RequiredMark';
import PublishImageUploadZone from '@/shein-platform/modules/products/components/PublishImageUploadZone';
import CustomAttributeValueModal from '@/shein-platform/modules/products/components/CustomAttributeValueModal';
import SheinUploadSourceMenu from '@/shein-platform/components/SheinUploadSourceMenu';
import { appendPrintNamesAfterHyphen } from '@/pages/ipe/workbench/utils/platformListingItemMedia';
import {
  attributeDisplayLabel,
  attributeValueDisplayLabel,
} from '@/shein-platform/utils/sheinAttributeTemplate';
import {
  resolveSheinPrintRiskMeta,
  sheinPrintRiskBadgeClass,
} from '@/shein-platform/utils/sheinPublishPrintInfringement';
import FieldHelpMark from '@/components/FieldHelpMark';
import LibraryModalContent from '@/pages/ipe/workbench/modals/LibraryModalContent';
import VideoSpacePreviewModal from '@/pages/ipe/workbench/components/VideoSpacePreviewModal';
import { appendUniqueImageSpaceRows } from '@/pages/ipe/workbench/utils/imageSpacePageFetch';
import { videoSpaceRecordToUploadFile } from '@/pages/ipe/workbench/utils/videoSpaceLibraryPick';
import { resolveVideoSpacePlayUrl } from '@/pages/ipe/workbench/utils/downloadVideoSpaceVideo';
import { SHEIN_PRODUCT_IMAGE_HINT } from '@/shein-platform/utils/sheinProductImage';
import { sheinPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';

/** 细节图最多张数（主图单独 1 张） */
const MAX_DETAIL_IMAGES = 10;

/** 印花图最多张数（站内字段，用于货号/SKU 拼接名称，不提交 SHEIN image_info） */
const MAX_PRINT_IMAGES = 10;

/** Shein 商品视频：仅 MP4，≤100MB */
const MAX_PRODUCT_VIDEO_BYTES = 100 * 1024 * 1024;
const SHEIN_PUBLISH_VIDEO_ACCEPT = 'video/mp4,.mp4';
const SHEIN_PRODUCT_VIDEO_HINT = '仅 MP4，≤100MB，无水印；上传后平台转换，绑定当前已选站点';

const SHEIN_SKC_PRODUCT_IMAGE_HINT = `商品图（主图/细节图）：${SHEIN_PRODUCT_IMAGE_HINT}。支持本地文件或图片空间上传。`;

const BATCH_FIELD_META = {
  price: { title: '批量填写价格', placeholder: '如 9.99', inputMode: 'decimal' },
  stock: { title: '批量填写库存', placeholder: '如 100', inputMode: 'numeric' },
  supplierSku: { get title() { return fixedUi("批量填写商家 SKU"); }, placeholder: '以 - 结尾，保存时拼印花名', inputMode: 'text' },
};

const BATCH_TITLE_KEY = {
  price: 'publishSpec.batchPrice',
  stock: 'publishSpec.batchStock',
  supplierSku: 'publishSpec.batchMerchantSku',
};
const BATCH_PH_KEY = {
  price: 'publishSpec.phPrice',
  stock: 'publishSpec.phStock',
  supplierSku: 'publishSpec.phSheinSku',
};

function sanitizeSkuPart(value) {
  return String(value ?? '').trim();
}

/** 从规格印花图槽位取出可拼接的名称（多张用 +；无名则空） */
export function patternNameFromPrintImages(images) {
  return (Array.isArray(images) ? images : [])
    .map((img) => sanitizeSkuPart(img?.name))
    .filter(Boolean)
    .join('+');
}

/** 弹窗未填印花名时，用规格印花图名称；都没有则为空 */
export function resolveSupplierSkuPatternName(input, printImages) {
  return sanitizeSkuPart(input) || patternNameFromPrintImages(printImages);
}

/** SPU-颜色编码-尺码-印花图名称；无印花名时仍以 - 结尾 */
export function buildMerchantSupplierSku({ spu, colorCode, sizeLabel, patternName }) {
  const prefix = [spu, colorCode, sizeLabel].map(sanitizeSkuPart).filter(Boolean).join('-');
  if (!prefix) return '';
  const pattern = sanitizeSkuPart(patternName);
  return `${prefix}-${pattern}`.slice(0, 200);
}

/** 从已有商家 SKU 推断颜色编码（SPU-颜色-尺码-…） */
export function inferColorCodeFromSupplierSku(supplierSku, spuPrefix) {
  const raw = sanitizeSkuPart(supplierSku);
  const spu = sanitizeSkuPart(spuPrefix).replace(/-+$/, '');
  if (!raw || !spu) return '';
  const normalized = raw.endsWith('-') ? raw.slice(0, -1) : raw;
  const parts = normalized.split('-').filter(Boolean);
  const spuParts = spu.split('-').filter(Boolean);
  if (parts.length <= spuParts.length) return '';
  return parts[spuParts.length] || '';
}

export function inferColorCodesFromSkcList(skcList, spuPrefix) {
  const codes = {};
  (Array.isArray(skcList) ? skcList : []).forEach((skc) => {
    if (!skcHasColor(skc)) return;
    const key = skcColorKey(skc);
    if (!key || codes[key]) return;
    const firstSku = (skc.skuRows || []).find((row) => sanitizeSkuPart(row?.supplierSku));
    if (!firstSku) return;
    const inferred = inferColorCodeFromSupplierSku(firstSku.supplierSku, spuPrefix);
    if (inferred) codes[key] = inferred;
  });
  return codes;
}

export function patternNamesFromPatternText(patternName) {
  return sanitizeSkuPart(patternName)
    .split('+')
    .map((part) => part.trim())
    .filter(Boolean);
}

export function appendPatternNameToSupplierSku(supplierSku, patternName) {
  const names = patternNamesFromPatternText(patternName);
  if (!names.length) return supplierSku;
  return appendPrintNamesAfterHyphen(supplierSku, names, { maxLen: 200 });
}

function skcListHasSupplierSku(skcList) {
  return (Array.isArray(skcList) ? skcList : []).some(
    (skc) => (skc.skuRows || []).some((row) => sanitizeSkuPart(row?.supplierSku)),
  );
}

function resolveSkuSizeLabel(sku, sizeOptions = []) {
  if (sku?.sizeIsCustom || sku?.sizeExtraValue) {
    return attributeValueDisplayLabel(sku.sizeLabel || sku.sizeExtraValue) || '';
  }
  return attributeValueDisplayLabel(
    sizeOptions.find((v) => String(v.id) === String(sku?.sizeValueId)) || sku?.sizeLabel,
  ) || '';
}

function PrintInfringementResultModal({ open, riskLabel, assessedAt, body, onClose }) {
  if (!open) return null;

  const formattedAt = assessedAt
    ? (() => {
      try {
        return new Date(assessedAt).toLocaleString();
      } catch {
        return assessedAt;
      }
    })()
    : '';

  return (
    <div
      className="spo-batch-modal"
      role="dialog"
      aria-modal="true"
      aria-label={specText('publishSpec.riskTitle', fixedUi("侵权检测结果"))}
    >
      <div className="spo-batch-modal__panel spo-batch-modal__panel--infringement">
        <div className="spo-batch-modal__head">
          <strong>{specText('publishSpec.riskTitle', fixedUi("侵权检测结果"))}</strong>
          <button type="button" className="spo-batch-modal__close" aria-label={specText('moduleShell.close', fixedUi("关闭"))} onClick={onClose}>×</button>
        </div>
        <div className="spo-batch-modal__body spo-infr-result-modal__body">
          <div className="spo-infr-result-modal__meta">
            <div><strong>{specText('publishSpec.riskStatus', fixedUi("检测状态："))}</strong>{riskLabel || '—'}</div>
            {formattedAt ? <div><strong>{specText('publishSpec.riskTime', fixedUi("检测时间："))}</strong>{formattedAt}</div> : null}
          </div>
          <div className="spo-infr-result-modal__detail">{body || specText('publishSpec.riskEmpty', fixedUi("暂无详细检测说明"))}</div>
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--primary spo-btn--sm" onClick={onClose}>{specText('moduleShell.close', fixedUi("关闭"))}</button>
        </div>
      </div>
    </div>
  );
}

function BatchFillModal({ field, currency, onClose, onConfirm, titleOverride }) {
  const meta = BATCH_FIELD_META[field] || BATCH_FIELD_META.price;
  const title = titleOverride || specText(BATCH_TITLE_KEY[field] || 'publishSpec.batchPrice', meta.title);
  const placeholder = specText(BATCH_PH_KEY[field] || 'publishSpec.fill', meta.placeholder);
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="spo-batch-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="spo-batch-modal__panel">
        <div className="spo-batch-modal__head">
          <strong>{title}{field === 'price' && currency ? ` (${currency})` : ''}</strong>
          <button type="button" className="spo-batch-modal__close" aria-label={specText('moduleShell.close', fixedUi("关闭"))} onClick={onClose}>×</button>
        </div>
        <div className="spo-batch-modal__body">
          <p className="spo-batch-modal__hint">{specText('publishSpec.applyAllRows', fixedUi("将应用到当前所有 SKU 行"))}</p>
          <input
            ref={inputRef}
            className="spo-input"
            value={value}
            inputMode={meta.inputMode}
            placeholder={placeholder}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onConfirm?.(value);
              }
            }}
          />
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose}>{specText('shop.cancel', fixedUi("取消"))}</button>
          <button
            type="button"
            className="spo-btn spo-btn--primary spo-btn--sm"
            onClick={() => onConfirm?.(value)}
          >
            {specText('moduleShell.confirmOk', '确定')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 商家 SKU 批量：SPU + 各颜色编码 + 印花图名称 → SPU-颜色编码-尺码-印花图；已有 SKU 时可仅追加印花名 */
function SupplierSkuBatchModal({
  colors = [],
  defaultSpu = '',
  printImages = [],
  skcList = [],
  onClose,
  onConfirm,
}) {
  const initialSpuRef = useRef(sanitizeSkuPart(defaultSpu));
  const initialColorCodesRef = useRef(
    inferColorCodesFromSkcList(skcList, defaultSpu),
  );
  const [spu, setSpu] = useState(() => initialSpuRef.current);
  const [patternName, setPatternName] = useState('');
  const [colorCodes, setColorCodes] = useState(() => {
    const inferred = initialColorCodesRef.current;
    const init = {};
    colors.forEach((c) => { init[c.key] = inferred[c.key] || ''; });
    return init;
  });
  const [error, setError] = useState('');
  const spuRef = useRef(null);
  const hasExistingSupplierSku = skcListHasSupplierSku(skcList);

  useEffect(() => {
    spuRef.current?.focus();
  }, []);

  const handleConfirm = () => {
    const nextSpu = sanitizeSkuPart(spu);
    const nextPattern = resolveSupplierSkuPatternName(patternName, printImages);
    const inferred = inferColorCodesFromSkcList(skcList, nextSpu || defaultSpu);
    const mergedCodes = {};
    colors.forEach((c) => {
      mergedCodes[c.key] = sanitizeSkuPart(colorCodes[c.key]) || inferred[c.key] || '';
    });
    const spuDirty = nextSpu !== sanitizeSkuPart(initialSpuRef.current);
    const colorsDirty = colors.some(
      (c) => sanitizeSkuPart(colorCodes[c.key]) !== sanitizeSkuPart(initialColorCodesRef.current[c.key]),
    );
    const useAppend = hasExistingSupplierSku && nextPattern && !spuDirty && !colorsDirty;

    if (useAppend) {
      onConfirm?.({ mode: 'append', patternName: nextPattern });
      return;
    }

    if (!nextSpu) {
      setError(specText('publishSpec.needSpuCode', '请填写 SPU 编码'));
      return;
    }
    if (!colors.length) {
      setError(specText('publishSpec.pickMainColors', '请先选择主规格颜色'));
      return;
    }
    const missing = colors.filter((c) => !mergedCodes[c.key]);
    if (missing.length) {
      setError(specText('publishSpec.needColorCodes', `请填写颜色编码：${missing.map((c) => c.label).slice(0, 3).join('、')}`, { names: missing.map((c) => c.label).slice(0, 3).join('、') }));
      return;
    }
    onConfirm?.({
      mode: 'rebuild',
      spu: nextSpu,
      patternName: nextPattern,
      colorCodes: mergedCodes,
    });
  };

  return (
    <div className="spo-batch-modal" role="dialog" aria-modal="true" aria-label={specText('publishSpec.batchMerchantSku', fixedUi("批量填写商家 SKU"))}>
      <div className="spo-batch-modal__panel spo-batch-modal__panel--supplier-sku">
        <div className="spo-batch-modal__head">
          <strong>{specText('publishSpec.batchMerchantSku', fixedUi("批量填写商家 SKU"))}</strong>
          <button type="button" className="spo-batch-modal__close" aria-label={specText('moduleShell.close', fixedUi("关闭"))} onClick={onClose}>×</button>
        </div>
        <div className="spo-batch-modal__body">
          <p className="spo-batch-modal__hint">
            {specText('publishSpec.sheinSkuHint', '按「SPU-颜色编码-尺码-印花图名称」拼接到各 SKU 行。已有商家 SKU 时，仅填写印花图名称会在当前 SKU 的 - 后追加（多张用 +）；重新填写 SPU 或颜色编码则整行重建。印花图名称可不填，默认用上方印花图名称；没有印花名时仍以 - 结尾')}
          </p>
          <div className="spo-field">
            <span className="spo-field__label">SPU {hasExistingSupplierSku ? null : <RequiredMark />}</span>
            <input
              ref={spuRef}
              className="spo-input"
              value={spu}
              placeholder={specText('publishSpec.phSpuFill', '填写 SPU 编码，如 1001')}
              onChange={(e) => setSpu(e.target.value)}
            />
          </div>
          <div className="spo-supplier-sku-colors">
            <span className="spo-field__label">{specText('publishSpec.colorCode', fixedUi("颜色编码"))}</span>
            {colors.length ? colors.map((c) => (
              <div key={c.key} className="spo-supplier-sku-color-row">
                <span className="spo-supplier-sku-color-row__name" title={c.label}>{c.label}</span>
                <input
                  className="spo-input"
                  value={colorCodes[c.key] || ''}
                  placeholder={specText('publishSpec.phKeepCode', '不填则沿用已有 SKU 中的编码')}
                  onChange={(e) => setColorCodes((prev) => ({ ...prev, [c.key]: e.target.value }))}
                />
              </div>
            )) : (
              <p className="spo-batch-modal__hint">{specText('publishSpec.noColors', fixedUi("暂无已选颜色"))}</p>
            )}
          </div>
          <div className="spo-field">
            <span className="spo-field__label">{specText('publishSpec.printName', fixedUi("印花图名称"))}</span>
            <input
              className="spo-input"
              value={patternName}
              placeholder={specText('publishSpec.phPrintName', '可选，不填则用上方印花图名称，多张用 + 拼接')}
              onChange={(e) => setPatternName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleConfirm();
                }
              }}
            />
          </div>
          {error ? <p className="spo-field__error" role="alert">{error}</p> : null}
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose}>{specText('shop.cancel', fixedUi("取消"))}</button>
          <button type="button" className="spo-btn spo-btn--primary spo-btn--sm" onClick={handleConfirm}>
            {specText('moduleShell.confirmOk', '确定')}
          </button>
        </div>
      </div>
    </div>
  );
}

function BatchHeader({ children, onBatch }) {
  const { t } = useTranslation('workbench');
  return (
    <th>
      <span className="spo-supply-th">
        <span>{children}</span>
        <button type="button" className="spo-supply-th__batch" onClick={onBatch}>
          {t('publishSpec.batch')}
        </button>
      </span>
    </th>
  );
}

// 槽位文案为站内自定义，非平台接口字段名（展示前去掉「(中文)」等脏标记）
function optionName(v) {
  return attributeValueDisplayLabel(v) || String(v?.id || '');
}

function isCustomSpecKey(key) {
  return String(key || '').startsWith('custom:');
}

function customLabelFromKey(key) {
  try {
    return decodeURIComponent(String(key).slice('custom:'.length));
  } catch {
    return String(key).slice('custom:'.length);
  }
}

export function makeCustomSpecKey(label) {
  return `custom:${encodeURIComponent(String(label || '').trim())}`;
}

/** SKC 颜色在选择器中的稳定 key（平台 valueId 或 custom:xxx） */
export function skcColorKey(skc) {
  if (!skc) return '';
  if (skc.colorIsCustom || skc.colorExtraValue) {
    return makeCustomSpecKey(skc.colorExtraValue || skc.colorLabel || '');
  }
  return String(skc.colorValueId || '');
}

export function skcHasColor(skc) {
  return Boolean(skc && (skc.colorValueId || String(skc.colorExtraValue || '').trim() || String(skc.colorLabel || '').trim()));
}

/**
 * 规格值选择。
 * - 默认：多选勾选（尺码仅选平台选项，不支持自定义）
 * - allowCustom + addMode：点选即添加（可连续选）；回车可添加自定义颜色
 *   （对齐商家后台「请选择或自定义」；OpenAPI 主规格自定义须用 custom_attribute_value，禁止 attribute_value_id=0）
 */
export function SpecValuePicker({
  options = [],
  value = [],
  onChange,
  placeholder = '请选择或搜索（可多选）',
  disabled = false,
  emptyText = fixedUi('暂无平台选项'),
  disabledHint = '暂不可选',
  allowCustom = false,
  addMode = false,
  /** key → 展示名；属性模板未就绪时用平台回填的 attributeValueName，禁止直接显示 valueId */
  labelByKey = {},
  /** 平台 has_permission=1：展示「申请新增」并走 add-custom-attribute-value */
  allowPlatformCustom = false,
  shopId = '',
  categoryId = '',
  attributeId = '',
  attributeLabel = '',
  onPlatformCustomAdded,
}) {
  const { t } = useTranslation('workbench');
  const rootRef = useRef(null);
  const controlRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [panelStyle, setPanelStyle] = useState({});
  const [customOpen, setCustomOpen] = useState(false);
  const [extraOptions, setExtraOptions] = useState([]);

  useEffect(() => {
    setExtraOptions([]);
    setCustomOpen(false);
  }, [attributeId, categoryId]);

  const normalizedOptions = useMemo(() => {
    const base = (options || []).map((opt) => ({ ...opt, id: String(opt.id) }));
    const seen = new Set(base.map((opt) => opt.id));
    const extras = extraOptions.filter((opt) => opt?.id && !seen.has(String(opt.id)));
    return extras.length
      ? [...base, ...extras.map((opt) => ({ ...opt, id: String(opt.id) }))]
      : base;
  }, [options, extraOptions]);

  const selectedKeys = useMemo(
    () => (Array.isArray(value) ? value.map(String).filter(Boolean) : []),
    [value],
  );

  const selectedTags = useMemo(() => selectedKeys.map((key) => {
    if (isCustomSpecKey(key)) {
      return {
        key,
        label: attributeValueDisplayLabel(customLabelFromKey(key)) || customLabelFromKey(key),
        custom: true,
      };
    }
    const opt = normalizedOptions.find((item) => item.id === key);
    const fallback = labelByKey[key];
    return {
      key,
      label: opt
        ? optionName(opt)
        : (attributeValueDisplayLabel(fallback) || fallback || key),
      custom: false,
    };
  }), [selectedKeys, normalizedOptions, labelByKey]);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return normalizedOptions;
    return normalizedOptions.filter((opt) => optionName(opt).toLowerCase().includes(q));
  }, [normalizedOptions, keyword]);

  const updatePanelPosition = () => {
    const el = controlRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const maxHeight = Math.min(280, Math.max(140, window.innerHeight - rect.bottom - 12));
    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 280),
      maxHeight,
      zIndex: 14000,
    });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePanelPosition();
    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, filtered.length, selectedKeys.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inRoot && !inPanel) {
        setOpen(false);
        setKeyword('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const addKey = (key) => {
    const nextKey = String(key || '').trim();
    if (!nextKey) return;
    if (selectedKeys.includes(nextKey)) return;
    onChange([...selectedKeys, nextKey]);
  };

  const toggle = (id) => {
    const nextId = String(id);
    if (addMode) {
      addKey(nextId);
      return;
    }
    const set = new Set(selectedKeys);
    if (set.has(nextId)) set.delete(nextId);
    else set.add(nextId);
    onChange([...set]);
  };

  const remove = (key) => onChange(selectedKeys.filter((item) => item !== String(key)));

  const addCustomFromKeyword = () => {
    const text = keyword.trim();
    if (!text) return false;
    const matched = normalizedOptions.find((opt) => optionName(opt).toLowerCase() === text.toLowerCase());
    if (matched) {
      addKey(matched.id);
      setKeyword('');
      return true;
    }
    if (allowCustom) {
      addKey(makeCustomSpecKey(text));
      setKeyword('');
      return true;
    }
    return false;
  };

  const canOpen = !disabled && (normalizedOptions.length > 0 || allowCustom);
  const inputPlaceholder = disabled
    ? disabledHint
    : (normalizedOptions.length === 0 && !allowCustom
      ? emptyText
      : placeholder);
  const canPlatformCustom = Boolean(
    allowPlatformCustom && shopId && categoryId && attributeId && !disabled,
  );

  const onPlatformAdded = (opt) => {
    if (!opt?.id) return;
    setExtraOptions((prev) => (
      prev.some((item) => String(item.id) === String(opt.id))
        ? prev
        : [...prev, { id: String(opt.id), name: opt.name, nameCn: opt.nameCn || opt.name }]
    ));
    onPlatformCustomAdded?.(opt);
    addKey(String(opt.id));
  };

  const panel = open && canOpen ? createPortal(
    <div
      ref={panelRef}
      className="spo-spec-picker__panel spo-spec-picker__panel--portal"
      style={panelStyle}
    >
      {filtered.length === 0 ? (
        <div className="spo-spec-picker__empty">
          {allowCustom && keyword.trim()
            ? t('publishSpec.enterCustom', { name: keyword.trim() })
            : t('productCopy.emptyOptions')}
        </div>
      ) : (
        <div className="spo-spec-picker__list">
          {filtered.map((opt) => {
            const checked = selectedKeys.includes(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                className={`spo-spec-picker__option${checked ? ' is-on' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  toggle(opt.id);
                  if (addMode) setKeyword('');
                }}
              >
                {!addMode ? (
                  <input type="checkbox" readOnly checked={checked} tabIndex={-1} />
                ) : null}
                <span>{optionName(opt)}</span>
                {addMode && checked ? <em className="spo-spec-picker__added">{t('publishSpec.added')}</em> : null}
              </button>
            );
          })}
        </div>
      )}
      <div className="spo-spec-picker__footer">
        {selectedKeys.length > 0 ? (
          <button
            type="button"
            className="spo-btn spo-btn--ghost spo-btn--sm"
            onClick={() => onChange([])}
          >
            {t('publishAttr.clear')}
          </button>
        ) : <span />}
        <button
          type="button"
          className="spo-btn spo-btn--secondary spo-btn--sm"
          onClick={() => {
            setOpen(false);
            setKeyword('');
          }}
        >
          {t('returnList.completed')}
        </button>
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <div
      className={`spo-spec-picker${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`}
      ref={rootRef}
    >
      <div className="spo-spec-picker__row">
        {selectedTags.length > 0 ? (
          <div className="spo-spec-picker__tags">
            {selectedTags.map((tag) => (
              <span key={tag.key} className={`spo-spec-picker__tag${tag.custom ? ' is-custom' : ''}`}>
                <em>{tag.label}</em>
                <button
                  type="button"
                  aria-label={t('productCopy.remove')}
                  disabled={disabled}
                  onClick={() => remove(tag.key)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : null}

        <div
          className="spo-spec-picker__control"
          ref={controlRef}
          onClick={() => {
            if (canOpen) setOpen(true);
          }}
        >
          <input
            className="spo-spec-picker__search"
            value={keyword}
            disabled={disabled || (!allowCustom && normalizedOptions.length === 0)}
            placeholder={inputPlaceholder}
            onFocus={() => {
              if (canOpen) setOpen(true);
            }}
            onChange={(e) => {
              setKeyword(e.target.value);
              if (canOpen) setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (addMode || allowCustom) {
                  if (!addCustomFromKeyword() && filtered[0]) addKey(filtered[0].id);
                } else if (filtered[0]) {
                  toggle(filtered[0].id);
                }
                setKeyword('');
              }
              if (e.key === 'Escape') {
                setOpen(false);
                setKeyword('');
              }
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <span className="spo-spec-picker__caret" aria-hidden>▾</span>
        </div>
      </div>
      {panel}
      {canPlatformCustom ? (
        <div className="spo-attr-custom-hint">
          <span>{t('publishAttr.noValue')}</span>
          <button
            type="button"
            className="spo-attr-custom-hint__link"
            onClick={() => setCustomOpen(true)}
          >
            {t('publishAttr.applyNew')}
          </button>
        </div>
      ) : null}
      <CustomAttributeValueModal
        open={customOpen}
        shopId={shopId}
        categoryId={categoryId}
        attributeId={attributeId}
        attributeLabel={attributeLabel}
        onClose={() => setCustomOpen(false)}
        onAdded={onPlatformAdded}
      />
    </div>
  );
}

/**
 * 规格供应：主规格 / 其他规格（名称来自类目销售属性模板，不写死「颜色/尺码」）
 * → 自动生成主规格图行 + 供应信息表
 */
function specText(key, fallback, options) {
  if (!i18n.isInitialized) return fallback;
  return i18n.t(key, { ns: 'workbench', defaultValue: fallback, ...(options || {}) }) || fallback;
}

export function specSupplyPriceLabel(priceMode, currency) {
  const code = String(currency || '').trim();
  if (priceMode === 'sale') {
    return code
      ? specText('publishSpec.salePriceCode', `售价(${code}) *`, { code })
      : specText('publishSpec.salePrice', '售价 *');
  }
  if (priceMode === 'cost') {
    return code
      ? specText('publishSpec.costPriceCode', `成本价(${code}) *`, { code })
      : specText('publishSpec.costPrice', '成本价 *');
  }
  return code
    ? specText('publishSpec.priceCode', `价格(${code}) *`, { code })
    : specText('publishSpec.price', '价格 *');
}

export default function SpecSupplySection({
  colorAttr,
  sizeAttr,
  skcList = [],
  selectedSizeIds = [],
  shopId,
  categoryId = '',
  customAttrPerm = {},
  onCustomValueAdded,
  onColorsChange,
  onSizesChange,
  onUpdateSkc,
  onUpdateSku,
  onRemoveSku,
  onBatchUpdateSku,
  onSyncSkuSupply,
  printImages = [],
  onPrintImagesChange,
  /** Shein 商品视频 product_video_list（最多 1 条） */
  sheinVideos = [],
  videoError = '',
  onVideosChange,
  /** 已选站点（发品绑定时使用；UI 不展示） */
  selectedSites: _selectedSites,
  currency = '',
  currencyPending = false,
  attrLoading = false,
  /** 编辑已审商品：价走专用接口，UI 提示文案 */
  isEdit = false,
  /** 'cost' | 'sale' | '' */
  priceMode = '',
  priceEditable = true,
  priceLockedHint = '',
  /** 批量商家 SKU：SPU 默认值（如货号） */
  defaultSupplierSkuSpu = '',
  printInfringement = null,
  printInfringementBusy = false,
  onAssessPrintInfringement,
  infringementCreditHint = '',
  /**
   * 官方产品模板：无店铺，上传只落 IPE，跳过 SHEIN 格式转换。
   */
  skipSheinConvert = false,
}) {
  const { t } = useTranslation('workbench');
  const videoInputRef = useRef(null);
  const videoUploadAnchorRef = useRef(null);
  const imageSpaceRowsRef = useRef([]);
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoMenuOpen, setVideoMenuOpen] = useState(false);
  const [videoLibraryOpen, setVideoLibraryOpen] = useState(false);
  const [videoLibrarySelection, setVideoLibrarySelection] = useState([]);
  const [videoPreviewTarget, setVideoPreviewTarget] = useState(null);
  const colorOptions = colorAttr?.values || [];
  const sizeOptions = sizeAttr?.values || [];
  const colorLabel = attributeDisplayLabel(colorAttr) || t('publishSpec.mainSpec');
  const sizeLabel = attributeDisplayLabel(sizeAttr) || t('publishSpec.otherSpec');
  const priceLabel = specSupplyPriceLabel(priceMode, currency);
  const priceBatchTitle = priceMode === 'sale'
    ? t('publishSpec.batchSale')
    : priceMode === 'cost'
      ? t('publishSpec.batchCost')
      : t('publishSpec.batchPrice');
  const selectedColorKeys = skcList.map(skcColorKey).filter(Boolean);
  const colorLabelByKey = useMemo(() => {
    const map = {};
    (skcList || []).forEach((skc) => {
      const key = skcColorKey(skc);
      const name = String(skc?.colorLabel || skc?.colorExtraValue || '').trim();
      if (key && name) map[key] = name;
    });
    return map;
  }, [skcList]);
  const platformSizeIds = (selectedSizeIds || []).map(String).filter(Boolean);
  const sizeLabelByKey = useMemo(() => {
    const map = {};
    (skcList || []).forEach((skc) => {
      (skc.skuRows || []).forEach((sku) => {
        if (sku.sizeIsCustom || sku.sizeExtraValue) {
          const key = makeCustomSpecKey(sku.sizeExtraValue || sku.sizeLabel || '');
          const name = String(sku.sizeLabel || sku.sizeExtraValue || '').trim();
          if (key && name) map[key] = name;
        } else if (sku.sizeValueId && sku.sizeLabel) {
          map[String(sku.sizeValueId)] = sku.sizeLabel;
        }
      });
    });
    return map;
  }, [skcList]);
  const coloredSkcs = skcList.filter(skcHasColor);
  const hasColors = coloredSkcs.length > 0;
  const [batchField, setBatchField] = useState(null);
  const [printRiskModalOpen, setPrintRiskModalOpen] = useState(false);
  const printRiskMeta = resolveSheinPrintRiskMeta(printInfringement);
  const showProductVideo = typeof onVideosChange === 'function' && !skipSheinConvert;

  const rememberImageSpaceRows = (rows) => {
    imageSpaceRowsRef.current = appendUniqueImageSpaceRows(imageSpaceRowsRef.current, rows);
  };

  const uploadVideoFile = async (file, options = {}) => {
    if (!file || !shopId) return;
    if (file.size > MAX_PRODUCT_VIDEO_BYTES) {
      showToast(specText('publishSpec.videoTooBig100', '视频需 ≤100MB'), 'warning');
      return;
    }
    const mime = String(file.type || '').toLowerCase();
    const name = String(file.name || '').toLowerCase();
    const looksMp4 = mime === 'video/mp4' || /\.mp4$/i.test(name);
    if (!looksMp4) {
      showToast(specText('publishSpec.videoMp4Only', '商品视频仅支持 MP4'), 'warning');
      return;
    }
    setVideoBusy(true);
    try {
      const res = await sheinPlatformAPI.uploadProductVideo(shopId, file, {
        fromVideoSpace: options.fromVideoSpace === true,
      });
      const videoUrl = String(res?.videoUrl || res?.sheinUrl || '').trim();
      if (!videoUrl) {
        throw new Error(specText('publishSpec.videoNoConvert', '视频上传未返回平台转换地址'));
      }
      onVideosChange?.([{
        videoUrl,
        sheinUrl: String(res?.sheinUrl || videoUrl).trim(),
        ipeUrl: res?.ipeUrl || options.sourceIpeUrl || '',
        taskId: res?.taskId || '',
        name: res?.name || file.name || '',
      }]);
      showToast(specText('publishSpec.videoOk', '视频上传成功'), 'success');
    } catch (err) {
      showToast(err?.message || specText('publishSpec.videoFail', '视频上传失败'), 'error');
    } finally {
      setVideoBusy(false);
    }
  };

  const handleVideoLibraryConfirm = async (ids) => {
    setVideoLibraryOpen(false);
    setVideoLibrarySelection([]);
    const id = Array.isArray(ids) ? ids[0] : ids;
    if (!id) return;
    const row = imageSpaceRowsRef.current.find((item) => String(item?.id) === String(id));
    if (!row) {
      showToast(specText('publishSpec.videoMissing', '未找到所选视频空间文件'), 'error');
      return;
    }
    try {
      const sourceIpeUrl = resolveVideoSpacePlayUrl(row)
        || String(row?.outputVideoUrl || row?._playUrl || '').trim();
      const file = await videoSpaceRecordToUploadFile(row, 'video.mp4');
      await uploadVideoFile(file, { fromVideoSpace: true, sourceIpeUrl });
    } catch (err) {
      showToast(err?.message || specText('publishSpec.videoSpaceFail', '视频空间读取失败'), 'error');
    }
  };

  const supplierSkuColorOptions = useMemo(() => (
    (skcList || [])
      .filter(skcHasColor)
      .map((skc) => ({
        key: skcColorKey(skc),
        label: attributeValueDisplayLabel(skc.colorLabel || skc.colorExtraValue) || skc.colorLabel || specText('publishSpec.unnamedColor', '未命名颜色'),
      }))
      .filter((row) => row.key)
  ), [skcList]);

  const applyBatch = (raw) => {
    const text = String(raw ?? '').trim();
    if (!batchField) return;
    if (batchField === 'supplierSku') return;
    if (!text) {
      setBatchField(null);
      return;
    }
    onBatchUpdateSku?.({ [batchField]: text });
    setBatchField(null);
  };

  const applySupplierSkuBatch = (payload) => {
    const { mode, spu, patternName, colorCodes } = payload || {};
    if (mode === 'append') {
      onBatchUpdateSku?.((skc, sku) => {
        const existing = sanitizeSkuPart(sku?.supplierSku);
        if (!existing) return null;
        return {
          supplierSku: appendPatternNameToSupplierSku(existing, patternName),
        };
      });
      setBatchField(null);
      return;
    }
    onBatchUpdateSku?.((skc, sku) => {
      const colorKey = skcColorKey(skc);
      const colorCode = sanitizeSkuPart(colorCodes?.[colorKey]);
      const sizeName = resolveSkuSizeLabel(sku, sizeOptions);
      if (!colorCode || !sizeName) return null;
      return {
        supplierSku: buildMerchantSupplierSku({
          spu,
          colorCode,
          sizeLabel: sizeName,
          patternName,
        }),
      };
    });
    if (typeof onUpdateSkc === 'function' && colorCodes) {
      (Array.isArray(skcList) ? skcList : []).forEach((skc) => {
        if (!skcHasColor(skc)) return;
        const colorKey = skcColorKey(skc);
        const colorCode = sanitizeSkuPart(colorCodes?.[colorKey]);
        if (!colorCode) return;
        onUpdateSkc(skc.id, { colorCode });
      });
    }
    setBatchField(null);
  };

  return (
    <div className="spo-spec-supply">
      {/* —— 主规格：销售属性 + 商品图 —— */}
      <div className="spo-spec-block">
        {/* 印花图：主规格上方（原位置） */}
        <div className="spo-spec-print">
          <div className="spo-spec-print__head">
            <span className="spo-field__label">{t('publishSpec.print')}</span>
            <div className="spo-spec-print__actions">
              <button
                type="button"
                className="spo-btn spo-btn--danger spo-btn--sm spo-spec-print__risk-btn"
                disabled={printInfringementBusy || printRiskMeta.meta.assessing}
                title={infringementCreditHint || undefined}
                onClick={() => { void onAssessPrintInfringement?.(); }}
              >
                {printRiskMeta.meta.assessing ? t('publishSpec.assessing') : t('publishSpec.infringement')}
              </button>
              <button
                type="button"
                className={`spo-infr-risk-btn${printRiskMeta.canOpenRisk ? ' is-clickable' : ''}`}
                disabled={!printRiskMeta.canOpenRisk}
                onClick={() => {
                  if (!printRiskMeta.canOpenRisk) return;
                  setPrintRiskModalOpen(true);
                }}
              >
                <span className={`spo-infr-risk-badge ${sheinPrintRiskBadgeClass(printRiskMeta.meta.risk, printRiskMeta.meta.assessing)}`}>
                  {printRiskMeta.riskLabel}
                </span>
                {printRiskMeta.canOpenRisk ? (
                  <span className="spo-infr-risk-hint">{t('publishSpec.viewRisk')}</span>
                ) : null}
              </button>
            </div>
          </div>
          <PublishImageUploadZone
            compact
            labelInside
            label={t('publishSpec.print')}
            imageType={2}
            multiple
            maxCount={MAX_PRINT_IMAGES}
            uploadSlotFirst
            prependNew
            reorderable
            enforceSheinRules={false}
            skipSheinConvert={skipSheinConvert}
            value={Array.isArray(printImages) ? printImages : []}
            shopId={shopId}
            onChange={(imgs) => onPrintImagesChange?.(
              Array.isArray(imgs) ? imgs.slice(0, MAX_PRINT_IMAGES) : [],
            )}
          />
        </div>

        <h4 className="spo-publish__subhead">{t('publishSpec.mainSpec')}</h4>

        <div className="spo-spec-row">
          <div className="spo-spec-attr">
            <span className="spo-field__label">{colorLabel} <RequiredMark /></span>
            <SpecValuePicker
              options={colorOptions}
              value={selectedColorKeys}
              onChange={onColorsChange}
              labelByKey={colorLabelByKey}
              placeholder={attrLoading ? t('returnList.loading') : t('publishSpec.pickCustom', { label: colorLabel })}
              disabled={attrLoading || !colorAttr}
              emptyText={t('publishSpec.emptyEnter')}
              disabledHint={attrLoading ? t('returnList.loading') : t('publishSpec.loadAttr', { label: colorLabel })}
              allowCustom
              addMode
              allowPlatformCustom={!!customAttrPerm[String(colorAttr?.attributeId || '')]}
              shopId={shopId}
              categoryId={categoryId}
              attributeId={colorAttr?.attributeId || ''}
              attributeLabel={colorLabel}
              onPlatformCustomAdded={(opt) => onCustomValueAdded?.(colorAttr?.attributeId, opt)}
            />
          </div>

          <div className="spo-spec-attr">
            <span className="spo-field__label">{sizeLabel}{sizeAttr?.required ? <RequiredMark /> : null}</span>
            <SpecValuePicker
              options={sizeOptions}
              value={platformSizeIds}
              onChange={onSizesChange}
              labelByKey={sizeLabelByKey}
              placeholder={attrLoading ? t('returnList.loading') : t('publishSpec.pickLabel', { label: sizeLabel })}
              disabled={attrLoading || !hasColors || !sizeAttr}
              emptyText={t('publishSpec.emptyNone')}
              disabledHint={!hasColors ? t('publishSpec.pickFirst', { label: colorLabel }) : (!sizeAttr ? t('publishSpec.noAttr', { label: sizeLabel }) : t('publishSpec.unavailable'))}
            />
          </div>
        </div>

        {hasColors ? (
          <div className="spo-publish__table-wrap spo-skc-img-wrap">
            <table className="spo-publish__table spo-skc-img-table">
              <thead>
                <tr>
                  <th className="spo-skc-img-table__color-head">{colorLabel}</th>
                  <th>
                    <span className="spo-th-with-help">
                      {t('publishSpec.productImages')}
                      <FieldHelpMark text={specText('publishSpec.productImagesHintShein', SHEIN_SKC_PRODUCT_IMAGE_HINT)} />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {coloredSkcs.map((skc) => (
                  <tr key={skc.id}>
                    <td className="spo-skc-img-table__color">
                      <strong title={skc.colorLabel || '—'}>{skc.colorLabel || '—'}</strong>
                      {skc.colorIsCustom ? (
                        <div className="spo-skc-img-table__skc-id">{t('publishSpec.custom')}</div>
                      ) : null}
                      {skc.skcCode ? (
                        <div className="spo-skc-img-table__skc-id">{skc.skcCode}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="spo-skc-img-table__details">
                        <PublishImageUploadZone
                          compact
                          labelInside
                          label={t('publishSpec.mainStar')}
                          imageType={1}
                          value={skc.mainImage ? [skc.mainImage] : []}
                          shopId={shopId}
                          skipSheinConvert={skipSheinConvert}
                          enforceSheinRules
                          onChange={(img) => onUpdateSkc(skc.id, {
                            mainImage: img || null,
                            // 方块图 / 色块图由刊登/发品提交时后端按主图自动派生，不再前端上传
                            squareImage: null,
                            colorBlockImage: null,
                          })}
                        />
                        <PublishImageUploadZone
                          compact
                          labelInside
                          label={t('publishSpec.detail')}
                          imageType={2}
                          multiple
                          maxCount={MAX_DETAIL_IMAGES}
                          uploadSlotFirst
                          prependNew
                          reorderable
                          value={Array.isArray(skc.detailImages) ? skc.detailImages : []}
                          shopId={shopId}
                          skipSheinConvert={skipSheinConvert}
                          enforceSheinRules
                          onChange={(imgs) => onUpdateSkc(skc.id, {
                            detailImages: Array.isArray(imgs) ? imgs.slice(0, MAX_DETAIL_IMAGES) : [],
                          })}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {/* 商品图下方、供应信息上方 */}
        {showProductVideo ? (
          <div className={`spo-field spo-spec-video${videoError ? ' spo-field--invalid' : ''}`} data-field-error="sheinVideos">
            <span className="spo-field__label spo-th-with-help">
              {specText('publishSpec.productVideo', '商品视频')}
              <FieldHelpMark text={specText('publishSpec.productVideoHintShein', SHEIN_PRODUCT_VIDEO_HINT)} />
            </span>
            <div className="spo-video-upload spo-video-upload--compact">
              {(Array.isArray(sheinVideos) ? sheinVideos : []).slice(0, 1).map((v) => {
                const playSrc = String(v.videoUrl || v.sheinUrl || v.ipeUrl || '').trim();
                return (
                  <div key={v.taskId || playSrc || v.ipeUrl} className="spo-video-upload__thumb">
                    {playSrc ? (
                      <video src={playSrc} muted preload="metadata" playsInline />
                    ) : (
                      <span className="spo-video-upload__placeholder">{specText('publishSpec.video', fixedUi("视频"))}</span>
                    )}
                    <button
                      type="button"
                      className="spo-img-upload__remove"
                      aria-label={specText('publishSpec.removeVideo', '移除视频')}
                      disabled={videoBusy}
                      onClick={() => onVideosChange([])}
                    >
                      ×
                    </button>
                    {playSrc ? (
                      <button
                        type="button"
                        className="spo-img-upload__preview"
                        onClick={() => setVideoPreviewTarget({
                          name: v.name || specText('publishSpec.productVideo', fixedUi("商品视频")),
                          videoUrl: playSrc,
                          outputVideoUrl: playSrc,
                          previewUrl: playSrc,
                        })}
                      >
                        {specText('publishSpec.preview', '预览')}
                      </button>
                    ) : null}
                  </div>
                );
              })}
              {!(sheinVideos || []).length ? (
                <button
                  ref={videoUploadAnchorRef}
                  type="button"
                  className={`spo-video-upload__drop${videoBusy ? ' spo-img-upload__drop--busy' : ''}`}
                  disabled={videoBusy || !shopId}
                  onClick={() => setVideoMenuOpen(true)}
                >
                  {videoBusy ? (
                    <span className="spo-img-upload__busy" aria-live="polite">
                      <span className="spo-img-upload__spinner" aria-hidden />
                      <span className="spo-img-upload__busy-text">{specText('publishSpec.uploading', fixedUi("上传中"))}</span>
                    </span>
                  ) : (
                    <>
                      <span className="spo-img-upload__slot-label">{specText('publishSpec.productVideo', fixedUi("商品视频"))}</span>
                      <span className="spo-img-upload__slot-action">{specText('publishSpec.clickUpload', fixedUi("点击上传"))}</span>
                    </>
                  )}
                </button>
              ) : null}
              <input
                ref={videoInputRef}
                type="file"
                accept={SHEIN_PUBLISH_VIDEO_ACCEPT}
                hidden
                disabled={videoBusy}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) await uploadVideoFile(file);
                }}
              />
              <SheinUploadSourceMenu
                open={videoMenuOpen}
                anchorRef={videoUploadAnchorRef}
                onClose={() => setVideoMenuOpen(false)}
                disabled={videoBusy}
                localLabel={specText('publishSpec.localUpload', '本地上传')}
                imageSpaceLabel={specText('publishSpec.videoSpace', '视频空间')}
                onPickLocal={() => videoInputRef.current?.click()}
                onPickImageSpace={() => setVideoLibraryOpen(true)}
              />
              <LibraryModalContent
                isOpen={videoLibraryOpen}
                mediaKind="video"
                onClose={() => {
                  setVideoLibraryOpen(false);
                  setVideoLibrarySelection([]);
                }}
                images={[]}
                selection={videoLibrarySelection}
                onToggle={(id) => {
                  setVideoLibrarySelection((prev) => (
                    prev.includes(id) ? [] : [id]
                  ));
                }}
                maxSelection={1}
                onConfirm={handleVideoLibraryConfirm}
                onImagesAppended={rememberImageSpaceRows}
                uploadOriginSource="upload"
                showToast={showToast}
                deferUsageBump
                selectAllLabel={specText('publishSpec.selectLoaded', '全选已加载')}
                closeOnBackdropClick={false}
              />
              <VideoSpacePreviewModal
                open={Boolean(videoPreviewTarget)}
                video={videoPreviewTarget}
                navList={videoPreviewTarget ? [videoPreviewTarget] : []}
                onClose={() => setVideoPreviewTarget(null)}
              />
            </div>
            {videoError ? (
              <p className="spo-wizard__inline-error" role="alert">{videoError}</p>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* —— 供应信息 —— */}
      {hasColors ? (
        <div className="spo-spec-block">
          <h4 className="spo-publish__subhead">{t('publishSpec.supply')}</h4>
          {isEdit ? (
            <p className="spo-wizard__inline-hint">
              {priceMode === 'sale'
                ? ''
                : priceMode === 'cost'
                  ? t('publishSpec.costHint')
                  : t('publishSpec.priceHint')}
              {priceLockedHint ? ` ${priceLockedHint}` : ''}
            </p>
          ) : null}

          <div className="spo-publish__table-wrap">
            <table className="spo-publish__table spo-supply-table">
              <thead>
                <tr>
                  <th className="spo-supply-table__color">{colorLabel}</th>
                  <th className="spo-supply-table__size">{sizeLabel}</th>
                  {priceEditable ? (
                    <BatchHeader onBatch={() => setBatchField('price')}>
                      {withRequiredMark(priceLabel)}
                    </BatchHeader>
                  ) : (
                    <th>{withRequiredMark(priceLabel)}</th>
                  )}
                  <BatchHeader onBatch={() => setBatchField('stock')}>{t('productTable.stock')} <RequiredMark /></BatchHeader>
                  <BatchHeader onBatch={() => setBatchField('supplierSku')}>{t('publishSpec.merchantSku')}</BatchHeader>
                  <th className="spo-supply-table__sku-img">{t('publishSpec.skuImage')}</th>
                  <th className="spo-supply-table__actions">{t('productTable.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {coloredSkcs.flatMap((skc) => {
                  const rows = skc.skuRows?.length ? skc.skuRows : [{ id: `${skc.id}-empty` }];
                  return rows.map((sku, rowIndex) => {
                    const sizeName = sku.sizeIsCustom || sku.sizeExtraValue
                      ? (attributeValueDisplayLabel(sku.sizeLabel || sku.sizeExtraValue) || '—')
                      : (
                        attributeValueDisplayLabel(
                          sizeOptions.find((v) => String(v.id) === String(sku.sizeValueId))
                          || sku.sizeLabel,
                        ) || '—'
                      );
                    const canAct = Boolean(sku.id);
                    return (
                      <tr key={sku.id || `${skc.id}-${rowIndex}`}>
                        {rowIndex === 0 ? (
                          <td rowSpan={rows.length} className="spo-supply-table__color">
                            <strong title={skc.colorLabel || '—'}>{skc.colorLabel || '—'}</strong>
                            {skc.skcCode ? (
                              <div className="spo-skc-img-table__skc-id">{skc.skcCode}</div>
                            ) : null}
                          </td>
                        ) : null}
                        <td className="spo-supply-table__size">{sizeName}</td>
                        <td>
                          <div className="spo-price-cell">
                            <input
                              className="spo-input"
                              value={sku.price ?? ''}
                              disabled={!priceEditable}
                              title={!priceEditable ? (priceLockedHint || t('publishSpec.priceLockedTitle')) : undefined}
                              onChange={(e) => onUpdateSku(skc.id, sku.id, { price: e.target.value })}
                              placeholder="0.00"
                            />
                            {currency ? <span className="spo-price-cell__unit">{currency}</span> : null}
                          </div>
                        </td>
                        <td>
                          <input
                            className="spo-input"
                            value={sku.stock ?? ''}
                            onChange={(e) => onUpdateSku(skc.id, sku.id, { stock: e.target.value })}
                            placeholder={t('publishSpec.fill')}
                          />
                        </td>
                        <td>
                          <input
                            className="spo-input"
                            value={sku.supplierSku ?? ''}
                            onChange={(e) => onUpdateSku(skc.id, sku.id, { supplierSku: e.target.value })}
                            placeholder={specText('publishSpec.endHyphen', '以 - 结尾')}
                          />
                        </td>
                        <td className="spo-supply-table__sku-img">
                          <PublishImageUploadZone
                            compact
                            imageType={1}
                            value={sku.skuImage ? [sku.skuImage] : []}
                            shopId={shopId}
                            skipSheinConvert={skipSheinConvert}
                            enforceSheinRules
                            onChange={(img) => onUpdateSku(skc.id, sku.id, { skuImage: img })}
                          />
                        </td>
                        <td className="spo-supply-table__actions">
                          {canAct && rows.length > 1 ? (
                            <div className="spo-supply-actions">
                              <button
                                type="button"
                                className="spo-btn spo-btn--danger spo-btn--ghost spo-btn--sm spo-supply-del"
                                onClick={() => onRemoveSku(skc.id, sku.id)}
                              >
                                {t('shop.delete')}
                              </button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </div>

          {batchField === 'supplierSku' ? (
            <SupplierSkuBatchModal
              colors={supplierSkuColorOptions}
              defaultSpu={defaultSupplierSkuSpu}
              printImages={printImages}
              skcList={skcList}
              onClose={() => setBatchField(null)}
              onConfirm={applySupplierSkuBatch}
            />
          ) : batchField ? (
            <BatchFillModal
              field={batchField}
              currency={currency}
              titleOverride={batchField === 'price' ? priceBatchTitle : undefined}
              onClose={() => setBatchField(null)}
              onConfirm={applyBatch}
            />
          ) : null}
        </div>
      ) : null}

      <PrintInfringementResultModal
        open={printRiskModalOpen}
        riskLabel={printRiskMeta.riskLabel}
        assessedAt={printRiskMeta.meta.assessedAt}
        body={printRiskMeta.meta.riskAdvice}
        onClose={() => setPrintRiskModalOpen(false)}
      />
    </div>
  );
}
